import { Box, styled } from "@mui/material";

import { useAppSelector } from "app/redux-store-interactions";
import { useIsPhone } from "contexts/panelMode";
import { useCancelCurrentModeChanges } from "features/toolMode/useCancelCurrentModeChanges";
import { useConfirmCurrentModeChanges } from "features/toolMode/useConfirmCurrentModeChanges";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";

import { TOOLBAR_LAYOUT, TOOLBAR_Z_INDEX } from "../constants";
import { BottomToolbarExtension } from "./BottomToolbarExtension";
import { BottomToolbarGeneralContent } from "./BottomToolbarGeneralContent";
import { BottomToolbarTabs } from "./BottomToolbarTabs";
import { BottomToolbarToolControlsContent } from "./BottomToolbarToolControlsContent";
import { BottomToolbarToolsContent } from "./BottomToolbarToolsContent";

const ToolbarContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isPhone",
})<{ isPhone?: boolean }>`
    position: absolute;
    bottom: max(
        ${({ isPhone }) => (isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET)}px,
        env(safe-area-inset-bottom, 0px)
    );
    left: max(
        ${({ isPhone }) => (isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET)}px,
        env(safe-area-inset-left, 0px)
    );
    right: max(
        ${({ isPhone }) => (isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET)}px,
        env(safe-area-inset-right, 0px)
    );
    z-index: ${TOOLBAR_Z_INDEX.TOOLBAR};
    display: flex;
    align-items: flex-end;
    /* No gap - extension slides from under the tabs */
    pointer-events: none;
`;

/**
 * Mobile Bottom Toolbar
 *
 * Horizontal toolbar at the bottom of the screen with General/Tools tabs
 * and expandable extension area.
 *
 * Custom implementation for complex tab/extension UI requirements.
 */
export function BottomToolbar() {
    const activeToolMode = useAppSelector(selectActiveToolMode);
    const isPhone = useIsPhone();
    const confirm = useConfirmCurrentModeChanges();
    const cancel = useCancelCurrentModeChanges();

    const isToolActive = activeToolMode !== ProcoreToolMode.Default;

    return (
        <ToolbarContainer isPhone={isPhone}>
            {/* Main tabs (General/Tools or Save/Exit) */}
            <BottomToolbarTabs onSave={confirm} onExit={cancel} />

            {/* Expandable extension with buttons */}
            <BottomToolbarExtension
                generalContent={<BottomToolbarGeneralContent />}
                toolsContent={<BottomToolbarToolsContent />}
                toolControlsContent={isToolActive ? <BottomToolbarToolControlsContent toolId={activeToolMode} /> : null}
            />
        </ToolbarContainer>
    );
}
